/**
 * Navigator Tuning · Plan history filter bar (feature 130): user type, care stage, status,
 * config version, empty specialties and tunable-only.
 */
import type { ConfigVersion, PlanListFilters, PlanUserType } from '../types/navigator-tuning.type';

export const USER_TYPES: { value: PlanUserType | undefined; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'fp', label: 'FP' },
  { value: 'seeker', label: 'Seeker' },
  { value: 'seeker_ex_guest', label: 'Seeker (ex-guest)' },
  { value: 'guest', label: 'Guest' },
];

/** Mirrors the backend SESSION_STATUSES (plan-list.dto.ts). */
const STATUSES = [
  'IN_PROGRESS',
  'STAGE_DETECTED',
  'MATCHMAKING',
  'PLAN_GENERATED',
  'COMPLETED',
  'ABANDONED',
  'CONCIERGE_ROUTED',
];

const selectClass =
  'block h-10 rounded-lg border border-[#d9d3c7] bg-white px-2.5 text-sm font-normal text-[#1b2232]';
const filterLabelClass = 'flex flex-col gap-1 text-xs font-normal text-[#56607a]';
const checkboxLabelClass = 'flex items-center gap-2 text-sm text-[#1b2232]';
const checkboxClass = 'h-[18px] w-[18px] accent-bquest-green';

export function PlanHistoryFilters({
  filters,
  onChange,
  careStages = [],
  versions,
}: {
  filters: PlanListFilters;
  onChange: (next: PlanListFilters) => void;
  careStages?: { id: string; name: string }[];
  versions: ConfigVersion[];
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border-subtle bg-white px-4 py-3.5">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-normal text-[#56607a]">User type</span>
        <div className="flex gap-0.5 rounded-lg bg-[#f1ede6] p-[3px]">
          {USER_TYPES.map((t) => (
            <button
              key={t.label}
              type="button"
              aria-pressed={filters.userType === t.value}
              onClick={() => onChange({ userType: t.value })}
              className={`min-h-[34px] rounded-md px-3 text-[13px] font-semibold ${
                filters.userType === t.value
                  ? 'bg-white text-[#014d41] shadow-sm'
                  : 'text-[#56607a]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <label className={filterLabelClass}>
        Care stage
        <select
          className={selectClass}
          value={filters.careStageId ?? ''}
          onChange={(e) => onChange({ careStageId: e.target.value || undefined })}
        >
          <option value="">All stages</option>
          {careStages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      <label className={filterLabelClass}>
        Status
        <select
          className={selectClass}
          value={filters.status ?? ''}
          onChange={(e) => onChange({ status: e.target.value || undefined })}
        >
          <option value="">All</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <label className={filterLabelClass}>
        Config
        <select
          className={selectClass}
          value={filters.configVersion ?? ''}
          onChange={(e) => onChange({ configVersion: e.target.value || undefined })}
        >
          <option value="">All versions</option>
          {versions
            .filter((v) => v.versionNumber)
            .map((v) => (
              <option key={v.id} value={v.id}>
                v{v.versionNumber}
              </option>
            ))}
          <option value="legacy">Legacy</option>
        </select>
      </label>
      <label className={checkboxLabelClass}>
        <input
          type="checkbox"
          className={checkboxClass}
          checked={!!filters.hasEmptySpecialty}
          onChange={(e) => onChange({ hasEmptySpecialty: e.target.checked || undefined })}
        />
        Only plans with empty specialties
      </label>
      <label className={checkboxLabelClass}>
        <input
          type="checkbox"
          className={checkboxClass}
          checked={!!filters.tunable}
          onChange={(e) => onChange({ tunable: e.target.checked || undefined })}
        />
        Only tunable plans
      </label>
    </div>
  );
}
